// Safety patch for "cssRules" error on cross-origin stylesheets
// MUST be at the very top to catch early library initializations
if (typeof window !== 'undefined') {
  try {
    // 1. Patch CSSStyleSheet prototype
    const originalCssRules = Object.getOwnPropertyDescriptor(CSSStyleSheet.prototype, 'cssRules');
    if (originalCssRules) {
      Object.defineProperty(CSSStyleSheet.prototype, 'cssRules', {
        get: function() {
          try {
            return originalCssRules.get?.call(this) || [];
          } catch (e) {
            return [];
          }
        },
        configurable: true
      });
    }

    // 2. Patch HTMLStyleElement and HTMLLinkElement to handle .sheet being null or restricted
    const patchSheetProperty = (proto: any) => {
      const originalSheet = Object.getOwnPropertyDescriptor(proto, 'sheet');
      if (originalSheet) {
        Object.defineProperty(proto, 'sheet', {
          get: function() {
            try {
              const sheet = originalSheet.get?.call(this);
              // If sheet exists but is restricted, accessing its properties might throw
              if (sheet) {
                // Pre-verify cssRules access
                try {
                  const rules = sheet.cssRules; 
                  if (!rules) throw new Error('cssRules is null');
                } catch (e) {
                  // Fallback for restricted or null cssRules
                  return new Proxy(sheet || {}, {
                    get: (target, prop) => {
                      if (prop === 'cssRules') return [];
                      if (prop === 'insertRule') return () => 0;
                      if (prop === 'deleteRule') return () => {};
                      const val = (target as any)[prop];
                      return typeof val === 'function' ? val.bind(target) : val;
                    }
                  }) as unknown as CSSStyleSheet;
                }
              } else {
                // If sheet itself is null, provide a proxy that returns empty rules
                // This specifically targets the "reading 'cssRules' of null" error
                return new Proxy({}, {
                    get: (target, prop) => {
                      if (prop === 'cssRules') return [];
                      return undefined;
                    }
                }) as unknown as CSSStyleSheet;
              }
              return sheet;
            } catch (e) {
              return null;
            }
          },
          configurable: true
        });
      }
    };
    patchSheetProperty(HTMLStyleElement.prototype);
    patchSheetProperty(HTMLLinkElement.prototype);

    // 3. Patch document.styleSheets to filter out nulls and provide proxies
    const originalStyleSheets = Object.getOwnPropertyDescriptor(Document.prototype, 'styleSheets');
    if (originalStyleSheets) {
      Object.defineProperty(Document.prototype, 'styleSheets', {
        get: function() {
          const list = originalStyleSheets.get?.call(this);
          if (!list) return list;
          
          // Create a proxy that behaves like a StyleSheetList but never returns null
          return new Proxy(list, {
            get: (target, prop) => {
              if (prop === 'length') return target.length;
              if (typeof prop === 'string' && !isNaN(Number(prop))) {
                const sheet = target[Number(prop)];
                if (!sheet) {
                  return new Proxy({}, {
                    get: (_, p) => (p === 'cssRules' ? [] : undefined)
                  }) as unknown as CSSStyleSheet;
                }
                return sheet;
              }
              const val = (target as any)[prop];
              return typeof val === 'function' ? val.bind(target) : val;
            }
          });
        },
        configurable: true
      });
    }
  } catch (e) {
    // Silent catch for non-browser environments or restricted environments
  }
}

import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import ErrorBoundary from './ErrorBoundary';
import './index.css';

// Wrap <App /> in <ErrorBoundary> so any runtime render error becomes
// visible (ErrorBoundary fallback UI) instead of unmounting the entire
// React tree and leaving an empty <div id="root">. Without this, a single
// rendering exception produces a blank screen behind the body's CSS
// effects (synthwave CRT scanlines/grid) — which is exactly the symptom
// reported on psyde-chat.pages.dev.
createRoot(document.getElementById('root')!).render(
    <ErrorBoundary>
        <App />
    </ErrorBoundary>
);
